import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import ModelFactory from 'business/models/ModelFactory';
import {
  PFDocProteinFormulationIngredientsDTO,
  SeedDTO,
  SeedFormulationConrollerClient,
  SeedSolutionIngredientsDTO,
  SeedWellDTO,
  WellInfoClient,
  WellLayerIngredientDTO,
} from 'business/models/Models';
import RMTable from 'components/customComponents/Controls/RMTable';
import LabelTextBox from 'components/customComponents/LabelTextBox';
import { EISelectBox } from 'components/customComponents/Layers/EICheckBox';
import RecentImagedCarousel from 'components/UpdatedDashboard/CustomComponents/RecentImagedCarousel';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { useRMSelector } from 'redux/hooks';
import { getDateISOFormat, MaxNameLength, showAlertSnackBar } from 'utils/Global';
import WellService from 'business/services/WellService';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import './SeedStyles.scss';
import { IStyle } from 'Styles';
import { getLatestImageBatchId } from 'components/plateviewer/experiment/Optimization/IterativeScreenOptimizationHelper';
import { getRegionId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import { CustomCheckBox } from 'components/input';
import { WellIng, WellNavigationDetails, Drop } from 'components/Experiment/constants';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { FmlxBackdropLoading, FmlxDatePicker } from 'fmlx-common-ui';
import { useFileNotFoundModal } from 'CustomHooks/useFileNotFoundModal';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import { SEED_FORMULATION_MESSAGES } from 'constants/messages';
import {
  getSeedCreationFailedMessage,
  getSkippedWellNames,
  getSkippedWellsWarningMessage,
} from './utils';

export type SeedFormulationProps = {
  show?: boolean;
  setShow?: (boolean) => void;
  selectedWells?: WellNavigationDetails[];
  treeNodeId?: number;
  isNew?: boolean;
  setSeedName?: (seedName: string) => void;
  setIsSeedEdited?: (flag: boolean) => void;
  setAccessRightName?: (accessRightName: string) => void;
  accessRightName?: string;
};

const loadSeedDTO = async (treeNodeId) => {
  const seedformulationClient: SeedFormulationConrollerClient = ModelFactory(
    SeedFormulationConrollerClient,
    false,
    true,
  );
  const seedInfo: SeedDTO = await seedformulationClient.getSeedInfo(treeNodeId);
  return seedInfo;
};

const updateSeed = async (seedDto: SeedDTO, showFileNotFoundModal) => {
  const seedformulationClient: SeedFormulationConrollerClient = ModelFactory(
    SeedFormulationConrollerClient,
  );
  try {
    const saved = await seedformulationClient.updateSeed(seedDto);
    if (saved) {
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        'Seed Formulation saved successfully.',
      );
    } else {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Could not save due to unknown error',
      );
    }
  } catch (error) {
    if (error.status === 409) {
      //conflict
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Entity with same name exists already',
      );
    } else if (error.StatusCode === 404) {
      showFileNotFoundModal();
    } else {
      throw error;
    }
  }
};

const getIngListDiv = (value: string | number) => (
  <div className="dataStyle">{value}</div>
);

const styles: IStyle = {
  inputStyle: {
    marginLeft: '28px',
    width: '182px',
    fontSize: '12px',
    height: '26px',
  },

  inputStylePlateId: {
    marginLeft: '37px',
    width: '50px',
    fontSize: '12px',
  },

  labelStylePlateId: {
    marginLeft: '12px',
    width: '60px',
  },

  labelStyle: {
    marginLeft: '12px',
    width: '69px',
  },

  inputStyleDrop: {
    marginLeft: '13px',
    width: '50px',
    fontSize: '12px',
  },

  labelStyleDrop: {
    marginLeft: '23px',
  },

  solutionInputStyle: {
    marginLeft: '0.5rem',
  },

  solutionBoxLabelStyle: {
    marginLeft: '12px',
    width: '69px',
  },

  solutionBoxSelectStyle: {
    marginLeft: '28px',
    width: '11rem',
    fontSize: '12px',
    height: '26px',
  },

  addButtonStyle: {
    width: '2rem',
    height: '2rem',
    marginLeft: '25px',
    marginRight: '14px',
  },

  removeButtonStyle: {
    width: '44px',
    height: '44px',
    marginLeft: '0.6rem',
    backgroundColor: '#666666',
    borderRadius: '4px',
  },

  saveButtonCustomStyle: {
    backgroundColor: '#F7A246',
    width: '9.5rem',
  },

  blankImageStyle: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bolder',
    color: 'grey',
  },
};

interface seedData {
  name: string;
  id: number;
  seedDate: Date;
  seedPlateId: number;
  seedDropNumber: number;
  pfName: string;
  seedWellNumbers: string;
  pfIng: any;
  notesText: string;
  solutionWellId: number;
  useWellSolChecked: boolean;
  treeNodeId: number;
}

const seedPfColumns = [
  {
    Header: 'Type',
    accessor: 'type',
  },
  {
    Header: 'Name',
    accessor: 'name',
  },
  {
    Header: 'Conc. (mg/mL)',
    accessor: 'concMg',
  },
  {
    Header: 'Conc. (mM)',
    accessor: 'concMm',
  },
];

const defaultSeedData = {
  name: '',
  id: 0,
  seedDate: undefined,
  seedPlateId: 0,
  seedDropNumber: 0,
  pfName: '',
  seedWellNumbers: '',
  pfIng: [],
  notesText: '',
  solutionWellId: 0,
  useWellSolChecked: false,
  treeNodeId: 0,
};

const renderSeedConditionNameCell = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (React.isValidElement(value) && value.type === 'input') {
    return value;
  }

  let displayValue = '';
  if (typeof value === 'string' || typeof value === 'number') {
    displayValue = String(value);
  } else if (React.isValidElement(value)) {
    const children = (value.props as { children?: unknown })?.children;
    if (children !== null && children !== undefined) {
      displayValue = String(children);
    }
  }

  return (
    <div className="seedConditionTruncatedCell dataStyle" title={displayValue}>
      {displayValue}
    </div>
  );
};

export const SeedFormulationModal = forwardRef((props: SeedFormulationProps, ref) => {
  const {
    setShow,
    selectedWells,
    treeNodeId,
    isNew,
    setSeedName,
    setIsSeedEdited,
    setAccessRightName,
    accessRightName,
  } = props;
  const isReadOnly = !isNew && accessRightName !== ETreeNodeAccessRightNames.Modify;
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber } = selectedDropImageParameters || {};
  const [conditionWellId, setConditionWellId] = useState(0);
  const [selectedWellItems, setSelectedWellItems] = useState([]);
  const [wellLayerIngList, setWellLayerIngList] = useState([]);
  const [solutionWellLayerIngList, setSolutionWellLayerIngList] = useState([]);
  const [wellSolutionEditData, setWellSolutionEditData] = useState([[]]);
  const [isDirty, setIsDirty] = useState(false);
  const [solutionWellLayerIngListText, setSolutionWellLayerIngListText] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [seedDtoData, setSeedDtoData] = useState<seedData>(defaultSeedData);
  const [originalSeedData, setOriginalSeedData] = useState<seedData>(defaultSeedData);
  const [seedData, setSeedData] = useState<SeedDTO>();
  const [selectedSolutionIngredientsRows, setSelectedSolutionIngredientsRows] = useState(
    [],
  );
  const exp = useRMSelector((state) => state.experiment.expData);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getBlankImage = () => {
    return (
      <div id="seedformulationmodal-blankimage" style={styles.blankImageStyle}>
        {isLoading ? 'Loading...' : 'Image Not Available'}
      </div>
    );
  };
  const showFileNotFoundModal = useFileNotFoundModal();

  const seedConditionColumns = useMemo(
    () => [
      {
        Header: <div id="seedformulationmodal-conditioncolums-type">Type</div>,
        accessor: 'type',
      },
      {
        Header: <div id="seedformulationmodal-conditioncolums-name">Name</div>,
        accessor: 'name',
        Cell: ({ value }) => renderSeedConditionNameCell(value),
      },
      {
        Header: <div id="seedformulationmodal-conditioncolums-conc">Conc.</div>,
        accessor: 'conc',
      },
      {
        Header: <div id="seedformulationmodal-conditioncolums-concunit">Conc. Unit</div>,
        accessor: 'concUnit',
      },
    ],
    [],
  );

  useEffect(() => {
    if (isNew) {
      //new seed case
      if (!selectedWells?.length) {
        const id = 'seeduseeffect-alert-noselectedwells';
        document.body.setAttribute('data-active-alert', id);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Please select the desired well(s) to be the source for the new seed.',
        );
        setShow(false);
        return;
      }

      const plateId = selectedWells[0].plateID;
      const otherPlateWells = selectedWells.filter((w) => w.plateID !== plateId);
      if (otherPlateWells.length) {
        const id = 'seeduseeffect-alert-differentplate';
        document.body.setAttribute('data-active-alert', id);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'All source wells must come from the same plate. Please select the desired well(s) from the same plate to create a new seed.',
        );
        setShow(false);
        return;
      }

      if (selectedWells.length > 20) {
        const id = 'seeduseeffect-alert-toomanywells';
        document.body.setAttribute('data-active-alert', id);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Seeds can be created from a maximum of 20 wells. Please select fewer wells to create a new seed.',
        );
        setShow(false);
        return;
      }
    }
    const id = 'seeduseeffect-pageload-start';
    document.body.setAttribute('data-active-step', id);
    PageLoad();
  }, []);

  const setSeedEditState = useCallback(
    (flag) => {
      setIsDirty(flag);
      if (setIsSeedEdited) {
        setIsSeedEdited(flag);
      }
    },
    [setIsSeedEdited],
  );
  const PageLoad = useCallback(async () => {
    let seedDto: SeedDTO;
    setIsLoading(true);
    try {
      if (!isNew) {
        //edit case
        seedDto = await loadSeedDTO(treeNodeId);
        setAccessRightName(seedDto.accessRightName);
        setSeedInfo(seedDto);

        if (seedDto?.latestInspectionId) {
          await getImageList(seedDto);
        }

        if (setSeedName) {
          setSeedName(seedDto.name);
        }
      } else {
        // new case
        setIsDirty(true);
        seedDto = await createSeedDTO();
        if (!checkIfHasProtein(seedDto)) {
          const getWellInfo = (w: WellNavigationDetails) => {
            const well = getWell(w, exp);
            return well
              ? {
                  wellId: well.wellId,
                  rowLetter: well.rowLetter,
                  columnNumber: well.columnNumber,
                }
              : undefined;
          };
          const message = getSeedCreationFailedMessage(
            getSkippedWellNames(selectedWells, new Set(), getWellInfo),
          );
          throw { ErrorMessage: message };
        }
        seedDto = await generateSeedFormat(seedDto);
        // seedDto = await saveNewSeed(seedDto);

        if (seedDto.latestInspectionId) {
          await getImageList(seedDto);
        }
      }
    } catch (error) {
      if (error?.StatusCode === 404 || error?.status === 404) {
        const id = 'seedpageload-alert-couldnotcreateseed';
        document.body.setAttribute('data-active-alert', id);
        showFileNotFoundModal();
      } else {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          error?.ErrorMessage || 'Error loading Seed.',
        );
        if (isNew) {
          setShow(false);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [treeNodeId, isNew]);

  const generateSeedFormat = useCallback(
    async (seedDto: SeedDTO) => {
      const client = ModelFactory(SeedFormulationConrollerClient);
      const getWellInfo = (w: WellNavigationDetails) => {
        const well = getWell(w, exp);
        return well
          ? {
              wellId: well.wellId,
              rowLetter: well.rowLetter,
              columnNumber: well.columnNumber,
            }
          : undefined;
      };

      let seedFormat: SeedDTO;
      try {
        seedFormat = await client.generateSeedFormat(seedDto);
      } catch {
        const message = getSeedCreationFailedMessage(
          getSkippedWellNames(selectedWells, new Set(), getWellInfo),
        );
        throw { ErrorMessage: message };
      }
      setSeedInfo(seedFormat);
      setSeedData(seedFormat);
      return seedFormat;
    },
    [setSeedData, selectedWells, exp],
  );

  const checkIfHasProtein = (seedDto: SeedDTO) => {
    if (
      seedDto.proteinFormulationId === null ||
      seedDto.proteinFormulationId === undefined
    ) {
      return false;
    }
    return true;
  };

  const setSeedInfo = useCallback(
    (seedDto: SeedDTO) => {
      const newSeedData = {
        ...seedDtoData,
        name: seedDto?.name,
        id: seedDto?.id,
        seedDate: seedDto?.dateCreated,
        seedPlateId: seedDto?.plateId,
        seedDropNumber: seedDto?.wellDropNumber,
        seedWellNumbers: seedDto?.wellList,
        pfName: seedDto?.proteinFormulationDTO?.pfName,
        pfIng: seedDto?.proteinFormulationDTO?.proteinFormulationIngredient,
        notesText: seedDto?.notes,
        useWellSolChecked: seedDto?.wellSolutionWellId ? true : false,
        solutionWellId: seedDto?.wellSolutionWellId,
        treeNodeId: seedDto?.treeNodeId,
      };
      setSeedDtoData(newSeedData);
      setOriginalSeedData(newSeedData);
      setSeedData(seedDto);

      getSelectedWellItems(seedDto.selectedWells);

      loadSolutionWellIngredientsBySeed(seedDto);
    },
    [seedDtoData],
  );

  const createSeedDTO = useCallback(async () => {
    const seedDto = new SeedDTO();
    seedDto.id = seedDtoData.id;
    if (isNew) {
      //edit seed and save
      seedDto.captureProfileId = cpId;
      seedDto.plateId = plateID ?? selectedWells?.[0]?.plateID;

      const seedWellDtos: SeedWellDTO[] = [];

      selectedWells?.forEach((w) => {
        const seedWellDto = new SeedWellDTO();
        const well: WellIng = getWell(w, exp);
        if (!well) {
          return;
        }
        const selectedDropNumber = dropNumber ?? well.dropList?.[0]?.dropNumber;
        const sourcePlateId = seedDto.plateId ?? w.plateID;
        const regionId = getRegionId(
          exp.regions,
          well.wellNumber,
          selectedDropNumber,
          'Drop',
          sourcePlateId,
        );

        if (!seedDto.wellDropId) {
          if (selectedDropNumber) {
            seedDto.wellDropNumber = selectedDropNumber;
          }
          const drops: Drop[] =
            well.dropList?.filter((d) => d.dropNumber == selectedDropNumber) ?? [];
          if (drops.length) {
            const drop = drops[0];
            seedDto.wellDropId = drop.dropId;
            seedDto.wellDropNumber = drop.dropNumber;
            seedDto.proteinFormulationId = drop.proteinFormulationID;
          }
        }

        seedWellDto.wellId = well.wellId;
        seedWellDto.seedID = seedDto.id;
        seedWellDto.wellNumber = well.wellNumber;
        seedWellDto.wellDropRegionId = regionId;
        seedWellDtos.push(seedWellDto);
      });

      seedDto.seedWellDTOs = seedWellDtos;
      seedDto.latestInspectionId = exp?.inspections?.length
        ? getLatestImageBatchId(exp)
        : 0;

      if (exp.profiles.length > 0) {
        seedDto.captureProfileId = exp.profiles[0].id;
      }
    }

    const seedSolutionIngredientDTOList: SeedSolutionIngredientsDTO[] = [];
    if (seedDtoData.useWellSolChecked) {
      seedDto.wellSolutionWellId = seedDtoData.solutionWellId;
    } else {
      seedDto.wellSolutionWellId = undefined;
    }

    solutionWellLayerIngListText?.forEach((ing) => {
      seedSolutionIngredientDTOList.push(
        new SeedSolutionIngredientsDTO({
          seedId: seedDto.id,
          type: ing.type,
          name: ing.name,
          concentration: ing.conc,
          concentrationUnit: ing.concUnit,
        }),
      );
    });

    wellSolutionEditData.map((ing) => {
      if (ing.length) {
        const concValue =
          ing[2] !== undefined && ing[2] !== '' && !isNaN(Number(ing[2]))
            ? parseFloat(ing[2])
            : undefined;
        const seedSolutionIngredientDTO: SeedSolutionIngredientsDTO =
          new SeedSolutionIngredientsDTO({
            seedId: seedDto.id,
            type: ing[0],
            name: ing[1],
            concentration: concValue,
            concentrationUnit: ing[3],
          });
        seedSolutionIngredientDTOList.push(seedSolutionIngredientDTO);
      }
    });

    seedDto.seedSolutionIngredientList = seedSolutionIngredientDTOList;
    seedDto.notes = seedDtoData.notesText;
    seedDto.name = seedDtoData.name;
    seedDto.dateCreated = seedDtoData.seedDate;
    seedDto.treeNodeId = seedDtoData.treeNodeId;
    if (seedDtoData.seedPlateId) {
      seedDto.plateId = seedDtoData.seedPlateId;
    }

    return seedDto;
  }, [
    seedDtoData,
    seedData,
    wellNavigationDetails,
    selectedDropImageParameters,
    exp,
    isNew,
    solutionWellLayerIngListText,
    wellSolutionEditData,
  ]); //to be seen later

  const showAlertErrorTop = (message: string) => {
    showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, message);
  };

  const seedValidated = (seedDto: SeedDTO) => {
    if (!seedDto.name || seedDto.name.trim() === '') {
      const id = 'seedpageload-alert-couldnotcreateseed';
      document.body.setAttribute('data-active-alert', id);
      showAlertErrorTop(SEED_FORMULATION_MESSAGES.nameEmpty);
      return false;
    }

    if (!seedDto.dateCreated) {
      showAlertErrorTop(SEED_FORMULATION_MESSAGES.dateEmpty);
      return false;
    }

    for (const row of wellSolutionEditData) {
      if (!row?.length) {
        continue;
      }
      const [, , concStr, unitStr] = row;
      const hasAnyValue = row.some((value) => String(value ?? '').trim() !== '');

      if (hasAnyValue) {
        const trimmedConc = String(concStr ?? '').trim();
        const trimmedUnit = String(unitStr ?? '').trim();

        if (trimmedConc === '' || isNaN(Number(trimmedConc)) || Number(trimmedConc) < 0) {
          showAlertErrorTop(SEED_FORMULATION_MESSAGES.concInvalid);
          return false;
        }

        if (trimmedUnit === '') {
          showAlertErrorTop(SEED_FORMULATION_MESSAGES.concUnitEmpty);
          return false;
        }

        if (!isNaN(Number(trimmedUnit))) {
          showAlertErrorTop(SEED_FORMULATION_MESSAGES.concUnitNumeric);
          return false;
        }
      }
    }

    if (seedDto.seedSolutionIngredientList?.length) {
      for (const ing of seedDto.seedSolutionIngredientList) {
        const { concentration: conc, concentrationUnit } = ing;
        const unit = concentrationUnit ? String(concentrationUnit).trim() : '';

        if (!conc || isNaN(Number(conc)) || Number(conc) < 0) {
          showAlertErrorTop(SEED_FORMULATION_MESSAGES.concInvalid);
          return false;
        }

        if (unit === '') {
          showAlertErrorTop(SEED_FORMULATION_MESSAGES.concUnitEmpty);
          return false;
        }

        if (!isNaN(Number(unit))) {
          showAlertErrorTop(SEED_FORMULATION_MESSAGES.concUnitNumeric);
          return false;
        }
      }
    }

    return true;
  };

  const saveSeed = useCallback(async () => {
    const seedFormulationClient: SeedFormulationConrollerClient = ModelFactory(
      SeedFormulationConrollerClient,
    );
    const getWellInfo = (w: WellNavigationDetails) => {
      const well = getWell(w, exp);
      return well
        ? {
            wellId: well.wellId,
            rowLetter: well.rowLetter,
            columnNumber: well.columnNumber,
          }
        : undefined;
    };

    try {
      const formSeed = await createSeedDTO();
      seedData.wellSolutionWellId = formSeed.wellSolutionWellId;
      seedData.seedSolutionIngredientList = formSeed.seedSolutionIngredientList;
      seedData.dateCreated = formSeed.dateCreated;
      seedData.notes = formSeed.notes;
      if (seedValidated(seedData)) {
        const saveResult = await seedFormulationClient.saveSeedFormulation(seedData);
        if (saveResult !== null || (saveResult !== undefined && setShow)) {
          const warningMessage = getSkippedWellsWarningMessage(
            getSkippedWellNames(
              selectedWells,
              new Set(saveResult.selectedWells?.map((x) => x.id)),
              getWellInfo,
            ),
          );
          if (warningMessage) {
            showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, warningMessage);
          }
          showAlertSnackBar(
            EAlertType.SUCCESS,
            ESnackBarPosition.TOP,
            'Seed Formulation saved successfully.',
          );
          setShow(false);
        }
      }
    } catch {
      const message = getSeedCreationFailedMessage(
        getSkippedWellNames(selectedWells, new Set(), getWellInfo),
      );
      throw { ErrorMessage: message };
    }
  }, [exp, seedData, selectedWells, createSeedDTO]);

  const refSaveSeed = useCallback(async () => {
    const seedDto: SeedDTO = await createSeedDTO();
    if (seedValidated(seedDto)) {
      await updateSeed(seedDto, showFileNotFoundModal);
      if (setSeedName) {
        setSeedName(seedDto.name);
      }
      setSeedEditState(false);
      if (setShow) {
        setShow(false);
      }
    }
  }, [createSeedDTO]);

  const discardChanges = useCallback(() => {
    setSeedDtoData(originalSeedData);
    setSeedData((previousSeed) =>
      previousSeed
        ? new SeedDTO({ ...previousSeed, name: originalSeedData.name })
        : previousSeed,
    );
    setSeedEditState(false);
  }, [originalSeedData]);

  const refCancelSeed = () => {
    setShow(false);
  };

  const getWell = (wellDetail: WellNavigationDetails, docData: ExperimentDtoClient) => {
    const plates = docData.plateDto.filter((p) => p.plateId == wellDetail.plateID);
    if (plates.length > 0) {
      const currentPlate = plates[0];
      const wells = currentPlate?.ingredients?.wellList?.filter(
        (w) => w.wellNumber == wellDetail.wellNum,
      );
      if (wells?.length > 0) {
        return wells[0];
      }
    }
  };

  const getSeedPfData = useCallback(() => {
    if (!seedDtoData.pfIng) {
      return;
    }

    const seedPfData = seedDtoData.pfIng.map(
      (pi: PFDocProteinFormulationIngredientsDTO, i) => ({
        srno: i + 1,
        type: getIngListDiv(pi.proteinFormulationIngredientTypeId),
        name: getIngListDiv(pi.name),
        concMg: getIngListDiv(pi.concentrationmgmL),
        concMm: getIngListDiv(pi.concentrationmM),
      }),
    );

    return seedPfData;
  }, [seedDtoData]);

  const getSelectedWellItems = (wells) => {
    if (wells.length == 0) {
      return;
    }

    const selectedWellItems = wells.map((w) => ({
      value: w.id,
      name: w.rowLetter + w.columnNumber,
    }));
    setSelectedWellItems(selectedWellItems);

    loadConditionWellIngredients(selectedWellItems[0].value);
  };

  const onConditionsWellChanged = (e) => {
    const wellId = Number(e.target.value);
    setConditionWellId(wellId);
    loadConditionWellIngredients(wellId);
  };

  const loadConditionWellIngredients = async (wellId) => {
    const wellInfoClient: WellInfoClient = ModelFactory(WellInfoClient);
    const wellLayerIngredientDTO: WellLayerIngredientDTO[] =
      await wellInfoClient.getWellLayerIngredients(wellId);

    const wlIngList =
      wellLayerIngredientDTO &&
      wellLayerIngredientDTO.map((wi: WellLayerIngredientDTO, i) => ({
        type: getIngListDiv(wi.ingredientTypeName),
        name: getIngListDiv(wi.ingredientName),
        conc: getIngListDiv(wi.concentration),
        concUnit: getIngListDiv(wi.concentrationUnit),
      }));

    setWellLayerIngList(wlIngList);
  };

  const onSolutionWellChanged = (e) => {
    const solutionWellId = Number(e.target.value);
    setSeedDtoData({ ...seedDtoData, solutionWellId });
    loadSolutionWellIngredients(solutionWellId);
  };

  const loadSolutionWellIngredients = async (wellId) => {
    const wellInfoClient: WellInfoClient = ModelFactory(WellInfoClient);
    const wellLayerIngredientDTO: WellLayerIngredientDTO[] =
      await wellInfoClient.getWellLayerIngredients(wellId);

    const wlIngListHtml =
      wellLayerIngredientDTO &&
      wellLayerIngredientDTO.map((wi: WellLayerIngredientDTO, i) => ({
        type: getIngListDiv(wi.ingredientTypeName),
        name: getIngListDiv(wi.ingredientName),
        conc: getIngListDiv(wi.concentration),
        concUnit: getIngListDiv(wi.concentrationUnit),
      }));

    const wlIngList =
      wellLayerIngredientDTO &&
      wellLayerIngredientDTO.map((wi: WellLayerIngredientDTO, i) => ({
        type: wi.ingredientTypeName,
        name: wi.ingredientName,
        conc: wi.concentration,
        concUnit: wi.concentrationUnit,
      }));

    setSolutionWellLayerIngList(wlIngListHtml);
    setSolutionWellLayerIngListText(wlIngList);
  };

  const loadSolutionWellIngredientsBySeed = async (seedDto: SeedDTO) => {
    const wlIngListHtml =
      seedDto.seedSolutionIngredientList &&
      seedDto.seedSolutionIngredientList.map((ing: SeedSolutionIngredientsDTO, i) => ({
        type: getIngListDiv(ing.type),
        name: getIngListDiv(ing.name),
        conc: getIngListDiv(ing.concentration),
        concUnit: getIngListDiv(ing.concentrationUnit),
      }));

    const wlIngList =
      seedDto.seedSolutionIngredientList &&
      seedDto.seedSolutionIngredientList.map((ing: SeedSolutionIngredientsDTO, i) => ({
        type: ing.type,
        name: ing.name,
        conc: ing.concentration,
        concUnit: ing.concentrationUnit,
      }));

    setSolutionWellLayerIngListValue(wlIngListHtml);
    setSolutionWellLayerIngListText(wlIngList);
  };

  const onUseWellSolClicked = (e) => {
    const useWellSolChecked = !seedDtoData.useWellSolChecked;
    let solutionWellId = seedDtoData.solutionWellId;

    if (useWellSolChecked) {
      if (!solutionWellId) {
        solutionWellId = selectedWellItems?.[0]?.value;
      }
      loadSolutionWellIngredients(solutionWellId);
    } else {
      setSolutionWellLayerIngList([]);
      setSolutionWellLayerIngListText([]);
      solutionWellId = null;
    }

    setSeedEditState(true);
    setSeedDtoData({ ...seedDtoData, useWellSolChecked, solutionWellId });
  };

  const setSolutionWellLayerIngListValue = (ing) => {
    if (ing) {
      setSolutionWellLayerIngList(ing);
    } else {
      setSolutionWellLayerIngList([]);
    }
  };

  const onTypeChanged = (e) => {
    const idStr = String(e.target.id ?? '');
    const numPart = idStr.split('-').pop();
    const index = parseInt(numPart);
    const colName = e.target.name;
    const value = e.target.value;
    const editData = wellSolutionEditData;

    switch (colName) {
      case 'ingType':
        editData[index][0] = value;
        break;
      case 'ingName':
        editData[index][1] = value;
        break;
      case 'ingConc':
        if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
          e.target.value = editData[index]?.[2] ?? '';
          return;
        }
        editData[index][2] = value;
        break;
      case 'ingConcUnit':
        editData[index][3] = value;
        break;
      default:
        break;
    }

    setWellSolutionEditData(editData);
    setSeedEditState(true);
  };

  const onAddWellIngredientClicked = (e) => {
    let solWellIng = solutionWellLayerIngList;
    const editData = wellSolutionEditData;
    const index = editData.length;
    if (!editData[index]) {
      editData[index] = [];
    }

    const editIng = {
      type: (
        <input
          id={`editing-type-${index.toString()}`}
          name="ingType"
          className="inputFieldWhiteBackground w-[3rem]"
          onChange={onTypeChanged}
          maxLength={MaxNameLength.SEED_FORMULATION_INGREDIENT}
        />
      ),
      name: (
        <input
          id={`editing-name-${index.toString()}`}
          name="ingName"
          className="inputFieldWhiteBackground w-[4.4rem]"
          onChange={onTypeChanged}
          maxLength={MaxNameLength.SEED_FORMULATION_INGREDIENT}
        />
      ),
      conc: (
        <input
          id={`editing-conc-${index.toString()}`}
          name="ingConc"
          className="inputFieldWhiteBackground w-[3rem]"
          onChange={onTypeChanged}
        />
      ),
      concUnit: (
        <input
          id={`editing-conunit-${index.toString()}`}
          name="ingConcUnit"
          className="inputFieldWhiteBackground w-[3rem]"
          onChange={onTypeChanged}
          maxLength={MaxNameLength.SEED_FORMULATION_INGREDIENT}
        />
      ),
    };

    solWellIng = solWellIng.concat([editIng]);

    setSolutionWellLayerIngList(solWellIng);

    setSeedEditState(true);
  };

  const onRemoveWellIngredientClicked = (e) => {
    const solWellIngHtml = solutionWellLayerIngList.removeByIndexes(
      selectedSolutionIngredientsRows,
    );

    const solWellIng = solutionWellLayerIngListText.removeByIndexes(
      selectedSolutionIngredientsRows,
    );

    setSolutionWellLayerIngList(solWellIngHtml);
    setSolutionWellLayerIngListText(solWellIng);

    const editStartIndex =
      solutionWellLayerIngList.length -
      (solutionWellLayerIngList.length - solutionWellLayerIngListText.length);
    const editIndexesToRemove = selectedSolutionIngredientsRows
      .filter((item) => item >= editStartIndex)
      .map((item) => item - solutionWellLayerIngListText.length + 1);

    const editData = wellSolutionEditData.removeByIndexes(editIndexesToRemove);
    setWellSolutionEditData(editData);

    setSeedEditState(true);
    setSelectedSolutionIngredientsRows([]);
  };

  const onNotesChanged = (e) => {
    setSeedDtoData({ ...seedDtoData, notesText: e.target.value });
    setSeedEditState(true);
  };

  const getImageList = async (seedDto: SeedDTO) => {
    const wellService = new WellService();
    const wellImageList = [];

    const batchId = seedDto.latestInspectionId;

    for (const seedWellDto of seedDto.seedWellDTOs ?? []) {
      const regionId = seedWellDto.wellDropRegionId;

      if (!regionId) {
        continue;
      }

      const wellImage = await wellService.getSeedWellImage(
        seedDto.plateId,
        seedWellDto.wellNumber,
        batchId,
        seedDto.wellDropNumber,
        seedDto.captureProfileId,
        'Drop',
        regionId,
        false,
      );
      if (wellImage.wellImage) {
        wellImageList.push(wellImage.wellImage);
      }
    }

    setSelectedImages(wellImageList);

    return wellImageList;
  };

  const onSeedNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setSeedData((previousSeed) =>
      previousSeed ? new SeedDTO({ ...previousSeed, name }) : previousSeed,
    );
    setSeedDtoData((previousData) => ({ ...previousData, name }));
    setSeedEditState(true);
  };

  const onSeedDateChanged = (dateValue) => {
    const seedDate = dateValue ? new Date(dateValue) : undefined;
    setSeedData((previousSeed) =>
      previousSeed
        ? new SeedDTO({ ...previousSeed, dateCreated: seedDate })
        : previousSeed,
    );
    setSeedDtoData((previousData) => ({ ...previousData, seedDate }));
    setSeedEditState(true);
  };

  const seedDate = seedDtoData.seedDate ? getDateISOFormat(seedDtoData.seedDate) : '';
  const showSeedDateError = isDirty && !seedDate;

  useImperativeHandle(
    ref,
    () => ({
      refSaveSeed,
      discardChanges,
    }),
    [refSaveSeed, discardChanges],
  );

  const handleSelectionChange = useCallback((rowIndices) => {
    setSelectedSolutionIngredientsRows([...rowIndices]);
  }, []);

  return (
    <div id="seedformulationmodal-main-container" style={{ fontSize: '12px' }}>
      <FmlxBackdropLoading
        id="seedformulationmodal-backdroploading"
        description="Loading..."
        show={isLoading}
      />
      <Row id="seedformulationmodal-row">
        <Col id="seedformulationmodal-col-seedsource" style={{ width: '30%' }}>
          <div
            id="seedformulationmodal-seedsource-wrapper"
            className="flex flex-x-center idr-wrapper-content margin5 min-h-[22vh] h-auto"
          >
            <div id="seedformulationmodal-seedsource-heading" className="headingStyle">
              Seed Source
            </div>
            <div id="seedformulationmodal-seedsource-content" className="ml-2">
              <div id="seedformulationmodal-seedsource-name-container">
                <label
                  id="seedformulationmodal-seedsource-name-label"
                  style={styles.labelStyle}
                >
                  Seed Name
                </label>
                <input
                  id="seedformulationmodal-seedsource-name-input"
                  style={styles.inputStyle}
                  value={seedData?.name ?? ''}
                  type="text"
                  onChange={onSeedNameChanged}
                  readOnly={isReadOnly}
                />
              </div>
              <div
                id="seedformulationmodal-seedsource-date-container"
                className="flex flex-col"
              >
                <div className="flex items-center">
                  <label
                    id="seedformulationmodal-seedsource-date-label"
                    style={styles.labelStyle}
                  >
                    Date
                  </label>
                  <div
                    id="seedformulationmodal-seedsource-date-input"
                    className="inline-block align-middle [&_.fmlx-date-picker]:w-full [&_.MuiOutlinedInput-root]:h-[26px] [&_.MuiOutlinedInput-root]:min-h-[26px] [&_.MuiOutlinedInput-root]:text-[12px] [&_.MuiOutlinedInput-root]:p-0 [&_.MuiOutlinedInput-input]:h-[26px] [&_.MuiOutlinedInput-input]:p-[5px] [&_.MuiOutlinedInput-input]:text-[12px]"
                    style={styles.inputStyle}
                  >
                    <FmlxDatePicker
                      id="seedformulationmodal-seedsource-date-picker"
                      variant={FmlxDatePicker.Variant.DATE}
                      value={seedDate}
                      onChange={(e, value) => onSeedDateChanged(value)}
                      disabled={isReadOnly}
                      error={showSeedDateError}
                    />
                  </div>
                </div>
                {showSeedDateError ? (
                  <div
                    id="seedformulationmodal-seedsource-date-error"
                    className="text-[#d32f2f] text-[11px] leading-tight mt-0.5 mb-1 ml-[109px]"
                  >
                    {SEED_FORMULATION_MESSAGES.dateEmpty}
                  </div>
                ) : null}
              </div>
              <div
                id="seedformulationmodal-seedsource-plate-drop-container"
                className="flex"
                style={{ width: '343px' }}
              >
                <LabelTextBox
                  labelText="Plate ID"
                  inputStyle={styles.inputStylePlateId}
                  labelStyle={styles.labelStylePlateId}
                  inputValue={seedDtoData.seedPlateId}
                  disabled={true}
                ></LabelTextBox>
                <LabelTextBox
                  labelText="Drop"
                  inputStyle={styles.inputStyleDrop}
                  labelStyle={styles.labelStyleDrop}
                  inputValue={seedDtoData.seedDropNumber}
                  disabled={true}
                ></LabelTextBox>
              </div>
              <LabelTextBox
                labelText="Well"
                inputStyle={styles.inputStyle}
                labelStyle={styles.labelStyle}
                inputValue={seedDtoData.seedWellNumbers}
                disabled={true}
              ></LabelTextBox>
            </div>
          </div>
          <div
            id="seedformulationmodal-dropimage-wrapper"
            className="flex flex-x-center idr-wrapper-content margin5 dropImageSize h-70"
          >
            <div id="seedformulationmodal-dropimage-heading" className="headingStyle">
              Drop Image
            </div>
            {selectedImages?.length ? (
              <RecentImagedCarousel ImageList={selectedImages} />
            ) : (
              <div id="seedformulationmodal-dropimage-blank">{getBlankImage()}</div>
            )}
          </div>
        </Col>
        <Col id="seedformulationmodal-col-proteinformulation" style={{ width: '30%' }}>
          <div
            id="seedformulationmodal-proteinformulation-wrapper"
            className="flex idr-wrapper-content margin5 proteinFormulationSize"
          >
            <div
              id="seedformulationmodal-proteinformulation-heading"
              className="headingStyle"
            >
              Protein Formulation
            </div>
            <LabelTextBox
              labelText="Protein Name"
              inputStyle={styles.inputStyle}
              labelStyle={styles.labelStyle}
              inputValue={seedDtoData.pfName}
              disabled={true}
            ></LabelTextBox>

            <div
              id="seedformulationmodal-proteinformulation-tablecontainer"
              className="proteinFormulationDataSize"
            >
              <RMTable
                columns={seedPfColumns}
                tableClass="w-full"
                data={getSeedPfData() ?? []}
              />
            </div>
          </div>

          <div
            id="seedformulationmodal-conditions-wrapper"
            className="flex flex-space-between idr-wrapper-content margin5 conditionSize"
          >
            <div id="seedformulationmodal-conditions-heading" className="headingStyle">
              Conditions
            </div>
            <div id="seedformulationmodal-conditions-selectbox-container">
              <EISelectBox
                labelText="Well Number"
                selectedValue={conditionWellId}
                options={selectedWellItems}
                onChange={onConditionsWellChanged}
                labelStyle={styles.labelStyle}
                selectStyle={styles.inputStyle}
                disabled={!selectedWellItems.length || isReadOnly}
              ></EISelectBox>
            </div>

            <div
              id="seedformulationmodal-conditions-tablecontainer"
              className="conditionTableSize"
            >
              <RMTable
                columns={seedConditionColumns}
                tableClass="w-full seedConditionTable"
                data={wellLayerIngList}
              />
            </div>
          </div>
        </Col>
        <Col id="seedformulationmodal-col-stabilizingsolution" style={{ width: '30%' }}>
          <div
            id="seedformulationmodal-stabilizingsolution-wrapper"
            className="flex flex-space-between idr-wrapper-content margin5"
          >
            <div
              id="seedformulationmodal-stabilizingsolution-heading "
              className="headingStyle"
            >
              Stabilizing Solution Ingredients
            </div>
            <div
              id="seedformulationmodal-stabilizingsolution-fieldssetcontainer"
              className="flex flex-space-between items-center margin5"
            >
              <fieldset
                id="seedformulationmodal-stabilizingsolution-fieldset"
                className="flex flex-row items-center"
                disabled={!selectedWellItems.length}
              >
                <CustomCheckBox
                  id="seedformulationmodal-stabilizingsolution-wellSolCheck"
                  onChange={onUseWellSolClicked}
                  label=""
                  checked={seedDtoData.useWellSolChecked}
                  disabled={isReadOnly}
                />
                <EISelectBox
                  labelText="Use Well Solution"
                  selectedValue={seedDtoData.solutionWellId}
                  options={selectedWellItems}
                  onChange={onSolutionWellChanged}
                  labelStyle={styles.solutionBoxLabelStyle}
                  selectStyle={styles.solutionBoxSelectStyle}
                  disabled={isReadOnly}
                ></EISelectBox>
              </fieldset>
            </div>

            <div
              id="seedformulationmodal-stabilizingsolution-tablecontainer"
              className="solutionTableStyle"
            >
              {solutionWellLayerIngList && (
                <RMTable
                  tableClass="w-full seedConditionTable seedConditionTable--withCheckbox"
                  columns={seedConditionColumns}
                  selectedIds={selectedSolutionIngredientsRows}
                  onCheckboxSelectionChange={handleSelectionChange}
                  data={solutionWellLayerIngList}
                />
              )}
            </div>
            <div
              id="seedformulationmodal-stabilizingsolution-buttoncontainer"
              className="ingredientButtonContainer"
            >
              <RMCustomButton
                iconProps={{ name: 'SeedAddIng', width: '44px', height: '44px' }}
                contentContainerStyle={styles.addButtonStyle}
                onClick={onAddWellIngredientClicked}
                isDisabled={isReadOnly}
              />
              <RMCustomButton
                iconProps={{
                  name: 'Delete',
                  width: '24px',
                  height: '24px',
                  fill: 'white',
                }}
                contentContainerStyle={styles.removeButtonStyle}
                isDisabled={!selectedSolutionIngredientsRows?.length || isReadOnly}
                onClick={onRemoveWellIngredientClicked}
              />
            </div>
          </div>
          <div
            id="seedformulationmodal-notes-wrapper"
            className="flex flex-space-between idr-wrapper-content margin5 max-h-[14vh]"
          >
            <div id="seedformulationmodal-notes-heading" className="headingStyle">
              Notes
            </div>
            <div id="seedformulationmodal-notes-inputcontainer">
              <input
                id="seedformulationmodal-notes-input"
                className="notesStyle"
                onChange={onNotesChanged}
                value={seedDtoData.notesText}
                readOnly={isReadOnly}
              ></input>
            </div>
          </div>
        </Col>
      </Row>
      {isNew && (
        <Row id="seedformulationmodal-buttonrow" className="buttonRowStyle mb-2">
          <Col id="seedformulationmodal-buttonrow-left" className="buttonRowStyle" />
          <Col
            id="seedformulationmodal-buttonrow-middle"
            className="buttonRowStyle"
          ></Col>
          <Col id="seedformulationmodal-buttonrow-right" className="buttonRowStyle">
            <div
              id="seedformulationmodal-buttoncontainer"
              className="flex flex-space-between buttonContainerStyle mt-3"
            >
              <RMCustomButton
                contentContainerClass="btn-outline-yellow-wide"
                onClick={saveSeed}
                title="Save"
                isDisabled={!isDirty}
              />
              <RMCustomButton
                contentContainerClass="btn-outline-yellow-wide mr-3"
                onClick={refCancelSeed}
                title="Cancel"
              />
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
});
